import api from './api';

// ==========================================
// FSI API V2 TypeScript Interfaces
// ==========================================

// Module names for selective fetching
export type FSIV2Module =
    | 'basic'
    | 'financial'
    | 'compliance'
    | 'height'
    | 'margins'
    | 'roads'
    | 'geometry'
    | 'sba'
    | 'development_scenarios';

// Quick Summary Response (ultra-fast)
export interface QuickSummaryResponse {
    gid: number;
    fp_no: string;
    fps_code: string;
    area_sqm: number;
    zone_code: string;
    base_fsi: number;
    max_fsi: number;
    estimated_buildable_sqm: number;
    location: string;
    tps: string;
    road_width_m: number;
    premium_applicable: boolean;
}

// Location sub-interface
export interface PlotLocation {
    city: string;
    village: string;
    taluka: string;
    district: string;
    state: string;
}

// Basic Module Response
export interface BasicModuleData {
    plot_info: {
        gid: number;
        fp_no: string;
        fps_code: string;
        name: string;
        area_sqm: number;
        tps_no: string;
        tps_name: string;
        location: PlotLocation;
    };
    zone_info: {
        zone_code: string;
        zone_name: string;
        base_fsi: number;
        max_fsi: number;
        chargeable_fsi: number;
        authority: string;
        regulations: string;
    };
    road_info: {
        width_m: number;
        premium_applicable: boolean;
        premium_percentage: number;
        premium_fsi: number;
        road_band: string;
        road_name: string;
        distance_m: number;
        data_source: string;
    };
    buildable_area: {
        plot_area_sqm: number;
        fsi_breakdown: {
            base_fsi: number;
            chargeable_fsi: number;
            premium_fsi: number;
            total_fsi: number;
        };
        buildable_area: {
            base_buildable_sqm: number;
            chargeable_buildable_sqm: number;
            premium_buildable_sqm: number;
            total_buildable_sqm: number;
        };
        tp_deduction: {
            percentage: number;
            area_sqm: number;
            applied: boolean;
        };
    };
    summary: {
        total_buildable_sqm: number;
        effective_fsi: number;
        premium_applied: boolean;
        estimated_units: number;
    };
}

// Financial Module Response
export interface FinancialModuleData {
    jantri_rate_per_sqm: number;
    jantri_rate_source: string;
    charges_breakdown: {
        chargeable_fsi: {
            area_sqm: number;
            rate_per_sqm: number;
            total_cost: number;
        };
        premium_fsi: {
            area_sqm: number;
            rate_per_sqm: number;
            total_cost: number;
        };
        scrutiny_fees: {
            percentage: number;
            base_amount: number;
            total_cost: number;
        };
    };
    total_charges: number;
    cost_per_sqm_buildable: number;
    summary: {
        total_charges: number;
        cost_per_sqm: number;
        estimated_roi_impact: number;
    };
    disclaimer: string;
}

// Compliance Module Response
export interface ComplianceModuleData {
    compliant: boolean;
    issues: string[];
    warnings: string[];
    minimum_requirements: {
        min_plot_area_sqm: number;
        min_road_width_m: number;
        actual_plot_area_sqm: number;
        actual_road_width_m: number;
    };
}

// Height Module Response
export interface HeightModuleData {
    restrictions: {
        max_height_m: number;
        basis: string;
    };
    proposed_height: {
        height_m: number;
        compliant: boolean;
        status: string;
    };
    tall_building: {
        eligible: boolean;
        min_road_width_required_m: number;
        approvals_required?: string[];
    };
    notes?: string[];
}

// Margins Module Response
export interface MarginsModuleData {
    side_margin_m: number;
    rear_margin_m: number;
    front_margin_m: number;
    impact_analysis: {
        original_plot_area_sqm: number;
        buildable_footprint_sqm: number;
        margin_impact_pct: number;
    };
    rules?: string;
    notes?: string[];
}

// Roads Module Response
export interface RoadsModuleData {
    primary_road: {
        gid: number;
        name: string;
        width_m: number;
        distance_m: number;
        premium_applicable: boolean;
        premium_percentage: number;
    };
    nearby_roads: Array<{
        gid: number;
        name: string;
        width_m: number;
        distance_m: number;
    }>;
    analysis: {
        search_radius_m: number;
        roads_found_count: number;
        data_source: string;
    };
}

// Geometry Module Response
export interface GeometryModuleData {
    plot: {
        gid: number;
        geometry: GeoJSON.Polygon;
        centroid: {
            lat: number;
            lng: number;
        };
        bounds: [number, number, number, number];
        source: string;
    };
    fps?: {
        fps_code: string;
        name: string;
    };
    roads?: Array<{
        type: string;
        gid: number;
        name: string;
        width_m: number;
        geometry: GeoJSON.LineString;
    }>;
    analysis_area?: {
        radius_m: number;
    };
}

// SBA Module Response (Saleable Built-up Area)
export interface SBAModuleData {
    height_validation: {
        proposed_height_m: number;
        max_permissible_height_m: number;
        compliant: boolean;
        permissible_floors: number;
        floor_height_m: number;
    };
    layout_planning: {
        plot_dimensions: {
            width_m: number;
            length_m: number;
            plot_area_sqm: number;
        };
        margins: {
            margin_front_m: number;
            margin_side_m: number;
            margin_rear_m: number;
            total_margin_area_sqm: number;
        };
        buildable_footprint: {
            footprint_sqm: number;
            coverage_percentage: number;
        };
    };
    floor_analysis: {
        total_floors: number;
        floor_height_m: number;
        total_building_height_m: number;
        carpet_area_per_floor_sqm: number;
        circulation_area_per_floor_sqm: number;
        efficiency_ratio: number;
    };
    area_breakdown: {
        gross_built_up_area_sqm: number;
        total_carpet_area_sqm: number;
        total_circulation_area_sqm: number;
        saleable_area_sqm: number;
    };
    usage_recommendations: {
        unit_size_sqm: number;
        unit_size_original: {
            value: number;
            unit: string;
        };
        estimated_units: number;
        units_per_floor: number;
        parking_requirement: {
            car_parking_spaces: number;
            two_wheeler_spaces: number;
            total_parking_area_sqm: number;
        };
        common_area_sqm: number;
        amenities_area_sqm: number;
    };
    development_scenarios: Array<{
        tower_count: number;
        layout_type: string;
        feasibility: string;
        fsi_compliant: boolean;
        tower_configuration: {
            towers: number;
            footprint_per_tower_sqm: number;
            tower_spacing_m: number;
            floors_per_tower: number;
            units_per_tower: number;
            units_per_floor: number;
        };
        areas: {
            total_gross_built_up_sqm: number;
            total_carpet_area_sqm: number;
            total_saleable_area_sqm: number;
            carpet_area_per_floor_per_tower_sqm: number;
            efficiency_ratio: number;
        };
        units: {
            total_units: number;
            units_per_tower: number;
            units_per_floor_per_tower: number;
            unit_size_sqm: number;
            unit_size_original: { value: number; unit: string };
        };
        parking: {
            car_parking_spaces: number;
            two_wheeler_spaces: number;
            car_space_area_sqm: number;
            two_wheeler_space_area_sqm: number;
            total_parking_area_sqm: number;
        };
        notes: string[];
    }>;
}

// Development Scenarios Module Response
export interface FSIUtilizationScenario {
    fsi_utilization: string;
    fsi_utilization_id: string;
    description: string;
    requires_approval: boolean;
    cost_analysis: {
        scenario_name: string;
        buildable_area_sqm: number;
        buildable_area_sqft: number;
        base_cost_per_sqft: number;
        cost_multiplier: number;
        total_construction_cost: number;
        cost_per_sqm: number;
        cost_per_sqft: number;
    };
    floor_plate: {
        typical_floor_plate_sqm: number;
        reduced_floor_plate_sqm: number;
        total_floors: number;
        effective_buildable_area: number;
        reduction_applied: boolean;
        reduction_percentage: number;
    };
    revenue_potential: {
        saleable_area_sqft?: number;
        rentable_area_sqft?: number;
        estimated_sale_rate_per_sqft?: number;
        estimated_rent_per_sqft_monthly?: number;
        gross_revenue?: number;
        annual_rental_income?: number;
        construction_cost: number;
        gross_margin?: number;
        cap_rate?: number;
    };
}

export interface DevelopmentScenario {
    scenario_id: string;
    scenario_name: string;
    description: string;
    requirements: {
        min_area_sqft: number;
        max_floors: number;
        requires_lift?: boolean;
        requires_fire_safety?: boolean;
        basement_levels?: number;
    };
    fsi_utilization_scenarios: FSIUtilizationScenario[];
    best_scenario?: {
        fsi_utilization: string;
        reason: string;
        construction_cost: number;
        gross_revenue: number;
    };
}

export interface DevelopmentScenariosModuleData {
    plot_analysis: {
        plot_area_sqm: number;
        plot_area_sqft?: number;
        zone_code: string;
        total_buildable_sqm: number;
        estimated_floors: number;
    };
    zone_fsi_config: {
        base_fsi: number;
        max_paid_fsi: number;
        total_max_fsi: number;
        tdr_allowed: boolean;
        tdr_max_additional?: number;
    };
    fsi_utilization_types: Array<{
        id: string;
        name: string;
        description: string;
        includes: string[];
        cost_multiplier: number;
    }>;
    residential_scenarios: DevelopmentScenario[];
    commercial_scenarios: DevelopmentScenario[];
    recommendations: string[];
    summary: {
        total_residential_options: number;
        total_commercial_options: number;
        zone_allows_tdr: boolean;
        best_residential?: {
            type: string;
            margin: number;
            fsi_utilization: string;
        };
        best_commercial?: {
            type: string;
        };
    };
    notes: string[];
}

// Combined Analysis Response
export interface FSIV2AnalysisResponse {
    gid: number;
    zone_code: string;
    timestamp: string;
    modules_included: FSIV2Module[];
    basic?: BasicModuleData;
    financial?: FinancialModuleData;
    compliance?: ComplianceModuleData;
    height?: HeightModuleData;
    margins?: MarginsModuleData;
    roads?: RoadsModuleData;
    geometry?: GeometryModuleData;
    sba?: SBAModuleData;
    development_scenarios?: DevelopmentScenariosModuleData;
    error?: string;
    details?: string;
}

// Single Module Response
export interface SingleModuleResponse<T> {
    gid: number;
    zone_code: string;
    module: FSIV2Module;
    data: T;
    timestamp: string;
    error?: string;
}

// Custom Analysis Parameters
export interface CustomAnalysisParams {
    include: FSIV2Module[];
    parameters?: {
        building_height_m?: number;
        tp_deduction_pct?: number;
        jantri_rate_per_sqm?: number;
        land_use?: string;
        authority?: string;
        unit_size?: number;
        unit_measurement?: 'sqm' | 'sqft' | 'sqyd';
        num_towers?: number;
    };
}

// ==========================================
// FSI V2 API Service Class
// ==========================================

class FSIV2Service {
    private baseUrl = '/api/v2/fsi';

    /**
     * Get ultra-fast summary for a plot (minimal data)
     * Use for list views, cards, or quick checks
     */
    async getQuickSummary(gid: number): Promise<QuickSummaryResponse> {
        const response = await api.get<QuickSummaryResponse>(`${this.baseUrl}/${gid}/quick`);
        return response.data;
    }

    /**
     * Get analysis with selected modules
     * @param gid Plot GID
     * @param modules Array of modules to include, or 'all' for everything
     * @param params Optional parameters like building_height_m, unit_size, etc.
     */
    async getAnalysis(
        gid: number,
        modules: FSIV2Module[] | 'all' = ['basic'],
        params?: {
            building_height_m?: number;
            tp_deduction_pct?: number;
            jantri_rate_per_sqm?: number;
            land_use?: string;
            authority?: string;
            unit_size?: number;
            unit_measurement?: 'sqm' | 'sqft' | 'sqyd';
            num_towers?: number;
        }
    ): Promise<FSIV2AnalysisResponse> {
        const queryParams = new URLSearchParams();

        // Add modules
        if (modules === 'all') {
            queryParams.set('include', 'all');
        } else {
            queryParams.set('include', modules.join(','));
        }

        // Add optional parameters
        if (params) {
            if (params.building_height_m !== undefined) {
                queryParams.set('building_height_m', params.building_height_m.toString());
            }
            if (params.tp_deduction_pct !== undefined) {
                queryParams.set('tp_deduction_pct', params.tp_deduction_pct.toString());
            }
            if (params.jantri_rate_per_sqm !== undefined) {
                queryParams.set('jantri_rate_per_sqm', params.jantri_rate_per_sqm.toString());
            }
            if (params.land_use) {
                queryParams.set('land_use', params.land_use);
            }
            if (params.authority) {
                queryParams.set('authority', params.authority);
            }
            if (params.unit_size !== undefined) {
                queryParams.set('unit_size', params.unit_size.toString());
            }
            if (params.unit_measurement) {
                queryParams.set('unit_measurement', params.unit_measurement);
            }
            if (params.num_towers !== undefined) {
                queryParams.set('num_towers', params.num_towers.toString());
            }
        }

        const response = await api.get<FSIV2AnalysisResponse>(
            `${this.baseUrl}/${gid}?${queryParams.toString()}`
        );
        return response.data;
    }

    /**
     * Get a single module on-demand
     * Useful for progressive loading
     */
    async getSingleModule<T>(
        gid: number,
        module: FSIV2Module,
        params?: {
            building_height_m?: number;
            unit_size?: number;
            unit_measurement?: 'sqm' | 'sqft' | 'sqyd';
            num_towers?: number;
        }
    ): Promise<SingleModuleResponse<T>> {
        const queryParams = new URLSearchParams();

        if (params) {
            if (params.building_height_m !== undefined) {
                queryParams.set('building_height_m', params.building_height_m.toString());
            }
            if (params.unit_size !== undefined) {
                queryParams.set('unit_size', params.unit_size.toString());
            }
            if (params.unit_measurement) {
                queryParams.set('unit_measurement', params.unit_measurement);
            }
            if (params.num_towers !== undefined) {
                queryParams.set('num_towers', params.num_towers.toString());
            }
        }

        const url = queryParams.toString()
            ? `${this.baseUrl}/${gid}/modules/${module}?${queryParams.toString()}`
            : `${this.baseUrl}/${gid}/modules/${module}`;

        const response = await api.get<SingleModuleResponse<T>>(url);
        return response.data;
    }

    /**
     * Post custom analysis with complex parameters
     * Use for scenario comparisons or batch analysis
     */
    async postCustomAnalysis(
        gid: number,
        body: CustomAnalysisParams
    ): Promise<FSIV2AnalysisResponse> {
        const response = await api.post<FSIV2AnalysisResponse>(
            `${this.baseUrl}/${gid}/analyze`,
            body
        );
        return response.data;
    }

    /**
     * Check API health (no auth required)
     */
    async checkHealth(): Promise<{
        status: string;
        version: string;
        service: string;
        available_modules: FSIV2Module[];
    }> {
        const response = await api.get(`${this.baseUrl}/health`);
        return response.data;
    }

    // Convenience methods for common queries

    /**
     * Get basic FSI analysis only
     */
    async getBasicAnalysis(gid: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic']);
    }

    /**
     * Get financial analysis (includes basic)
     */
    async getFinancialAnalysis(gid: number, jantriRate?: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic', 'financial'], {
            jantri_rate_per_sqm: jantriRate
        });
    }

    /**
     * Get compliance check
     */
    async getCompliance(gid: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic', 'compliance']);
    }

    /**
     * Get height restrictions with proposed height validation
     */
    async getHeightAnalysis(gid: number, proposedHeight?: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic', 'height'], {
            building_height_m: proposedHeight
        });
    }

    /**
     * Get SBA analysis with unit size
     */
    async getSBAAnalysis(
        gid: number,
        unitSize?: number,
        unitMeasurement?: 'sqm' | 'sqft' | 'sqyd',
        numTowers?: number
    ): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic', 'sba'], {
            unit_size: unitSize,
            unit_measurement: unitMeasurement,
            num_towers: numTowers
        });
    }

    /**
     * Get development scenarios for financial deep-dive
     */
    async getDevelopmentScenarios(gid: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic', 'development_scenarios']);
    }

    /**
     * Get geometry for map visualization
     */
    async getGeometry(gid: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['geometry']);
    }

    /**
     * Get full investment analysis (basic + financial + development_scenarios)
     */
    async getInvestmentAnalysis(gid: number): Promise<FSIV2AnalysisResponse> {
        return this.getAnalysis(gid, ['basic', 'financial', 'development_scenarios']);
    }
}

// Export singleton instance
export const fsiV2Service = new FSIV2Service();

// Export class for testing
export { FSIV2Service };
